import { useQuery } from '@anchorage/graphql-react';
import type {
  QueryOptions as QueryHookOptions,
  QueryResult,
} from '@anchorage/graphql-react';
import type {
  DocumentNode,
  OperationVariables,
  TypedDocumentNode,
} from '@apollo/client';
import { NetworkStatus } from '@apollo/client';
import { useEffect, useEffectEvent, useMemo, useRef } from 'react';

import {
  DEFAULT_PAGE_SIZE,
  useCursorBasedPagination,
} from '../useCursorBasedPagination';
import type {
  PageInfo,
  PaginationVariables,
  TablePagination,
} from '../useCursorBasedPagination';
import { useDeepCompare } from '../useDeepCompare';
import { usePollingLifecycle } from '../usePollingLifecycle';

export type UsePaginatedQueryOptions<
  TData,
  TVariables extends OperationVariables,
> = {
  /** Non-pagination variables (filters, sort, IDs, etc.). Pagination variables are managed internally. */
  variables?: Omit<TVariables, 'first' | 'after' | 'last' | 'before'>;
  /** Number of items per page. Defaults to 10. */
  pageSize?: number;
  /** Extract PageInfo from query data. */
  getPageInfo: (data: TData) => PageInfo;
  /**
   * Poll interval in ms. Polling stops on error (with `errorPolicy: 'all'`,
   * only on an error without data) and while the tab is hidden, and resumes
   * without a refetch when the tab becomes visible.
   */
  pollInterval?: number;
  /** Skip query execution. */
  skip?: boolean;
  /** Apollo fetch policy. */
  fetchPolicy?: QueryHookOptions<TData, TVariables>['fetchPolicy'];
  /** Apollo error policy. */
  errorPolicy?: QueryHookOptions<TData, TVariables>['errorPolicy'];
  /** Apollo link context. */
  context?: QueryHookOptions<TData, TVariables>['context'];
  /**
   * Cursor variables for the first render, used verbatim instead of
   * `{ first: pageSize }`. Lets a caller reopen the page a user left.
   */
  initialPagination?: PaginationVariables;
  /** Called with the cursor variables whenever they change, including on mount. */
  onPaginationChange?: (pagination: PaginationVariables) => void;
};

export type UsePaginatedQueryResult<
  TData,
  TVariables extends OperationVariables,
> = {
  data: TData | undefined;
  loading: boolean;
  error: Error | undefined;
  tablePagination: TablePagination;
  resetPagination: () => void;
  refetch: QueryResult<TData, TVariables>['refetch'];
};

/**
 * Combines a query hook with cursor-based pagination.
 *
 * Replaces the Apollo `onCompleted`/`onError` callbacks (removed in Apollo 4)
 * with reactive pagination state synced from query data.
 *
 * @example
 * ```tsx
 * const { data, loading, error, tablePagination } = usePaginatedQuery(
 *   WalletAddressesDocument,
 *   {
 *     variables: { vaultID, walletID },
 *     pageSize: 25,
 *     getPageInfo: (data) => data.vault.balancesByAddress.pageInfo,
 *   },
 * );
 * ```
 */
export const usePaginatedQuery = <
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
>(
  document: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options: UsePaginatedQueryOptions<TData, TVariables>,
): UsePaginatedQueryResult<TData, TVariables> => {
  const {
    variables,
    pageSize = DEFAULT_PAGE_SIZE,
    getPageInfo,
    pollInterval,
    skip,
    fetchPolicy,
    errorPolicy,
    context,
    initialPagination,
    onPaginationChange,
  } = options;

  const {
    paginationVariables,
    tablePagination,
    resetPagination,
    updatePagination,
  } = useCursorBasedPagination(initialPagination ?? { first: pageSize });

  // Effect Event so an inline callback does not re-fire this on every render.
  const notifyPaginationChange = useEffectEvent(
    (pagination: PaginationVariables) => onPaginationChange?.(pagination),
  );
  useEffect(() => {
    notifyPaginationChange(paginationVariables);
  }, [paginationVariables]);

  const mergedVariables = useMemo(
    () =>
      ({
        ...variables,
        ...paginationVariables,
      }) as TVariables,
    [variables, paginationVariables],
  );

  const {
    data,
    loading: rawLoading,
    networkStatus,
    error,
    refetch,
    startPolling,
    stopPolling,
  } = useQuery<TData, TVariables>(document, {
    variables: mergedVariables,
    skip,
    pollInterval,
    fetchPolicy,
    errorPolicy,
    context,
  });

  // notifyOnNetworkStatusChange defaults to true, so raw `loading` is true on
  // every poll tick. Deriving keeps a genuine manual refetch visible;
  // usePollingQuery derives the same way but exposes it as `fetchState`.
  const loading = rawLoading && networkStatus !== NetworkStatus.poll;

  // A caller reading partial data (`errorPolicy: 'all'`) reads what resolved
  // next to the error, so its poll goes on while the answers carry data. An
  // answer with none is a failed backend and stops the poll, as any error
  // does under the other policies. usePollingQuery leaves that stop to its
  // caller, which can call `stopPolling`; this hook does not expose it.
  const pollingError =
    errorPolicy === 'all' && data != null ? undefined : error;

  usePollingLifecycle({
    pollInterval,
    skip,
    error: pollingError,
    stopOnError: true,
    refetchOnVisible: false,
    refetch,
    startPolling,
    stopPolling,
  });

  const queryPageInfo = data ? getPageInfo(data) : undefined;
  const [pageInfoSignal] = useDeepCompare([queryPageInfo]);
  const [variablesSignal] = useDeepCompare({ variables });
  const prevVariablesSignalRef = useRef<number | null>(null);
  useEffect(() => {
    const variablesChanged =
      prevVariablesSignalRef.current !== null &&
      prevVariablesSignalRef.current !== variablesSignal;
    prevVariablesSignalRef.current = variablesSignal;

    if (variablesChanged) {
      resetPagination();
    }

    if (queryPageInfo) {
      updatePagination(queryPageInfo);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [variablesSignal, pageInfoSignal, resetPagination, updatePagination]);

  return useMemo(
    () => ({
      data,
      loading,
      error,
      tablePagination,
      resetPagination,
      refetch,
    }),
    [data, loading, error, tablePagination, resetPagination, refetch],
  );
};
